import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from "@mui/material";
import { fetchClientPayments, PAYMENT_HISTORY_PAGE_SIZE } from "./gestionCarteraApi";
import { formatDate, formatMoney, formatYesNo } from "./gestionCarteraFormat";
import { formatQuotaNumbers, PAYMENT_STATUSES, paymentStatusBadge, toPaymentHistoryParams, WITHOUT_CREDIT } from "./historialPagosCatalog";
import { Card, cellSx, COLORS, CountPill, headCellSx, PrimaryButton, SectionTitle, SkeletonRows, StatusBadge, TableMessage } from "./gestionCarteraUi";

const EMPTY_FILTERS = { status: "", credit: "", from: "", to: "" };
/** Orden por día de pago y, en el mismo día, por ID del pago (lo resuelve backend, así "Cargar más" sigue el mismo orden). */
const DATE_ORDER = { desc: "DESC", asc: "ASC" };
const DATE_COLUMN = "Fecha de pago";
const PAYMENT_COLUMNS = ["ID pago", DATE_COLUMN, "Valor", "Recaudador", "Referencia del recaudador", "Crédito / cuota", "Estado", "Contabilizado", "Notas"];

/** La sección usa la fuente del módulo reducida un 10%: 14px → 12.6px y body1 de MUI (16px) → 14.4px. */
const FONT_SCALE = 0.9;
const TEXT_SIZE = 14 * FONT_SCALE;
const BODY_SIZE = 16 * FONT_SCALE;
const textSx = { fontSize: TEXT_SIZE };
const sectionHeadCellSx = { ...headCellSx, ...textSx };
const sectionCellSx = { ...cellSx, ...textSx };
const filterFieldSx = { "& .MuiInputBase-input, & .MuiInputLabel-root": { fontSize: BODY_SIZE } };
const filterSelectProps = { MenuProps: { PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: BODY_SIZE } } } } };

function CreditQuotasCell({ credits }) {
  if (credits.length === 0) return <Typography sx={{ fontWeight: 500, fontSize: BODY_SIZE }}>N/A</Typography>;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "auto auto", justifyContent: "center", columnGap: 2, rowGap: 0.5, textAlign: "left" }}>
      {credits.map((credit) => (
        <React.Fragment key={credit.creditUuid}>
          <Typography sx={{ fontWeight: 500, fontSize: BODY_SIZE, whiteSpace: "nowrap" }}>{`Crédito ${credit.creditOldId ?? "—"}`}</Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: BODY_SIZE }}>{formatQuotaNumbers(credit.quotaNumbers)}</Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

function NotesCell({ notes }) {
  if (!notes?.trim()) return "—";
  return (
    <Tooltip title={notes}>
      <Typography sx={{ maxWidth: 220, mx: "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: TEXT_SIZE }}>{notes}</Typography>
    </Tooltip>
  );
}

function PaymentRow({ payment }) {
  const badge = paymentStatusBadge(payment);
  return (
    <TableRow hover>
      <TableCell sx={sectionCellSx}>{payment.oldId ?? "—"}</TableCell>
      <TableCell sx={sectionCellSx}>{formatDate(payment.paymentDate)}</TableCell>
      <TableCell sx={sectionCellSx}>{formatMoney(payment.paymentValue)}</TableCell>
      <TableCell sx={sectionCellSx}>{payment.paymentCollector || "—"}</TableCell>
      <TableCell sx={{ ...sectionCellSx, wordBreak: "break-all" }}>{payment.referenceIdCollector || "—"}</TableCell>
      <TableCell sx={sectionCellSx}><CreditQuotasCell credits={payment.credits || []} /></TableCell>
      <TableCell sx={sectionCellSx}><StatusBadge tone={badge.tone} label={badge.label} sx={textSx} /></TableCell>
      <TableCell sx={sectionCellSx}>{formatYesNo(payment.accounted)}</TableCell>
      <TableCell sx={sectionCellSx}><NotesCell notes={payment.paymentNotes} /></TableCell>
    </TableRow>
  );
}

function PaymentFilters({ filters, credits, onChange, onClear }) {
  const set = (name) => (event) => onChange({ ...filters, [name]: event.target.value });
  const hasFilters = Object.values(filters).some(Boolean);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr) auto" }, gap: 1.5, alignItems: "center", px: { xs: "12px", sm: "20px" }, pb: 2 }}>
      <TextField select size="small" sx={filterFieldSx} SelectProps={filterSelectProps} label="Estado" value={filters.status} onChange={set("status")}>
        <MenuItem value="">Todos</MenuItem>
        {PAYMENT_STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
      </TextField>
      <TextField select size="small" sx={filterFieldSx} SelectProps={filterSelectProps} label="Crédito" value={filters.credit} onChange={set("credit")}>
        <MenuItem value="">Todos</MenuItem>
        {credits.map((credit) => <MenuItem key={credit.uuid} value={credit.uuid}>{`Crédito ${credit.oldId}`}</MenuItem>)}
        <MenuItem value={WITHOUT_CREDIT}>N/A (sin aplicar a crédito)</MenuItem>
      </TextField>
      <TextField size="small" sx={filterFieldSx} type="date" label="Desde" InputLabelProps={{ shrink: true }} value={filters.from} onChange={set("from")} />
      <TextField size="small" sx={filterFieldSx} type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={filters.to} onChange={set("to")} />
      <PrimaryButton onClick={onClear} disabled={!hasFilters} sx={{ minHeight: 40, ...textSx }}>Limpiar filtros</PrimaryButton>
    </Box>
  );
}

function PaymentHeadCell({ column, order, onToggleOrder }) {
  if (column !== DATE_COLUMN) return <TableCell sx={sectionHeadCellSx}>{column}</TableCell>;
  const direction = order === DATE_ORDER.asc ? "asc" : "desc";
  return (
    <TableCell sx={sectionHeadCellSx} aria-sort={direction === "asc" ? "ascending" : "descending"}>
      <TableSortLabel active direction={direction} onClick={onToggleOrder} sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}>
        {column}
      </TableSortLabel>
    </TableCell>
  );
}

/** Historial paginado: "Cargar más" agrega la siguiente página; cambiar filtros u orden reinicia. */
const usePaymentHistory = (clientUuid, filters, order) => {
  const [state, setState] = useState({ status: "loading", items: [], page: 0, last: true, totalElements: 0 });
  const latestRequest = useRef(0);
  const params = useMemo(() => ({ ...toPaymentHistoryParams(filters), order }), [filters, order]);

  const load = useCallback(
    async (page) => {
      const requestId = ++latestRequest.current;
      setState((current) => ({ ...current, status: page === 0 ? "loading" : "loadingMore" }));
      try {
        const data = await fetchClientPayments({ clientUuid, params, page });
        if (requestId !== latestRequest.current) return;
        setState((current) => ({
          status: "ready",
          items: page === 0 ? data.content : [...current.items, ...data.content],
          page,
          last: data.last,
          totalElements: data.totalElements,
        }));
      } catch (error) {
        if (requestId === latestRequest.current) setState((current) => ({ ...current, status: "error" }));
      }
    },
    [clientUuid, params]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return [state, load];
};

/**
 * Historial de pagos (payment_records) del cliente: pagos con su documento + pagos aplicados a sus
 * créditos. `credits`: todos los créditos del cliente (para el filtro por crédito).
 */
export default function HistorialPagosSection({ clientUuid, credits }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [order, setOrder] = useState(DATE_ORDER.desc);
  const [history, loadHistory] = usePaymentHistory(clientUuid, filters, order);
  const toggleOrder = () => setOrder((current) => (current === DATE_ORDER.desc ? DATE_ORDER.asc : DATE_ORDER.desc));

  const renderBody = () => {
    if (history.status === "loading") return <SkeletonRows columns={PAYMENT_COLUMNS.length} rows={3} />;
    if (history.status === "error") {
      return <TableMessage columns={PAYMENT_COLUMNS.length} role="alert" titleSx={textSx} title="No pudimos cargar el historial de pagos" action={<PrimaryButton sx={textSx} onClick={() => loadHistory(0)}>Reintentar</PrimaryButton>} />;
    }
    if (history.items.length === 0) return <TableMessage columns={PAYMENT_COLUMNS.length} titleSx={textSx} title="No hay pagos con estos filtros" />;
    return history.items.map((payment) => <PaymentRow key={payment.uuid} payment={payment} />);
  };

  return (
    <Card component="section" aria-label="Historial de pagos" sx={{ overflow: "hidden" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, p: { xs: "12px", sm: "16px 20px" } }}>
        <SectionTitle sx={textSx}>Historial de pagos</SectionTitle>
        {history.status !== "loading" && history.status !== "error" && <CountPill value={history.totalElements} sx={textSx} />}
        {history.status !== "loading" && history.status !== "error" && (
          <Typography sx={{ ...textSx, color: COLORS.muted }}>{`Mostrando ${history.items.length} de ${history.totalElements} pagos`}</Typography>
        )}
      </Box>
      <PaymentFilters filters={filters} credits={credits} onChange={setFilters} onClear={() => setFilters(EMPTY_FILTERS)} />
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1200 }} aria-label="Historial de pagos">
          <TableHead>
            <TableRow>{PAYMENT_COLUMNS.map((column) => <PaymentHeadCell key={column} column={column} order={order} onToggleOrder={toggleOrder} />)}</TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </TableContainer>
      {!history.last && history.status !== "loading" && history.status !== "error" && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <PrimaryButton sx={textSx} onClick={() => loadHistory(history.page + 1)} disabled={history.status === "loadingMore"}>
            {history.status === "loadingMore" ? "Cargando…" : `Cargar ${PAYMENT_HISTORY_PAGE_SIZE} más`}
          </PrimaryButton>
        </Box>
      )}
    </Card>
  );
}
